// src/pages/LandingPage.jsx
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Paper,
} from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from '../components/ContactForm';

// Iconos
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PeopleIcon from '@mui/icons-material/People';
import DuoIcon from '@mui/icons-material/Duo';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InstagramIcon from '@mui/icons-material/Instagram';
import XIcon from '@mui/icons-material/X';
import PinterestIcon from '@mui/icons-material/Pinterest';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import LinkIcon from '@mui/icons-material/Link';
import PaymentIcon from '@mui/icons-material/Payment';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import DiamondIcon from '@mui/icons-material/Diamond';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import doctoraHero from '../assets/doctora_hero.png';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ShieldIcon from '@mui/icons-material/Shield';
import VerifiedIcon from '@mui/icons-material/Verified';
import logo from '../assets/tu_logo.png';

/* ─── Paleta basada en el logo ─── */
const COLORS = {
  white:      '#ffffff',
  red:        '#f3353e',
  redDark:    '#d42d35',
  redLight:   '#ff6b72',
  navy:       '#164d70',
  navyDark:   '#0e3550',
  navyLight:  '#1d6490',
  sky:        '#6cabd3',
  skyLight:   '#9ac5e2',
  skyPale:    '#e8f4fb',
  skyDark:    '#4a8db8',
  bg:         '#f7fafd',
  bgCard:     '#ffffff',
  textPrimary:'#0e2a3f',
  textSecondary:'#5a7d95',
};

const MotionBox = motion(Box);
const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

/* ─── Glassmorphism Card ─── */
const GlassCard = styled(Box)(() => ({
  background: alpha(COLORS.white, 0.65),
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  borderRadius: 28,
  border: `1.5px solid ${alpha(COLORS.sky, 0.25)}`,
  boxShadow: `0 8px 32px ${alpha(COLORS.navy, 0.08)}, 0 1.5px 6px ${alpha(COLORS.navy, 0.04)}`,
}));

/* ─── Keyframes ─── */
const float = keyframes`
  0%,100%{transform:translateY(0) rotate(0deg)}
  50%{transform:translateY(-18px) rotate(3deg)}
`;
const pulse = keyframes`
  0%,100%{transform:scale(1);opacity:1}
  50%{transform:scale(1.04);opacity:.85}
`;
const gradientShift = keyframes`
  0%{background-position:0% 50%}
  50%{background-position:100% 50%}
  100%{background-position:0% 50%}
`;

/* ─── Partículas ─── */
const Particle = ({ x, y, size, color, delay }) => (
  <MotionBox
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      filter: 'blur(1.5px)',
      pointerEvents: 'none',
    }}
    animate={{
      opacity: [0, 0.5, 0],
      y: [-40, -180],
      x: [0, (Math.random() - 0.5) * 80],
    }}
    transition={{
      duration: 12 + Math.random() * 8,
      repeat: Infinity,
      delay,
      ease: 'easeInOut',
    }}
    style={{ left: `${x}%`, top: `${y}%` }}
  />
);

/* ═══════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
   ═══════════════════════════════════════════════════════════════ */
const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  /* ── Partículas ── */
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: 30 + Math.random() * 60,
    size: Math.random() * 5 + 2,
    color: i % 3 === 0
      ? alpha(COLORS.red, 0.25)
      : i % 3 === 1
        ? alpha(COLORS.sky, 0.3)
        : alpha(COLORS.white, 0.35),
    delay: i * 0.6,
  }));

  /* ── Features ── */
  const features = [
    { icon: <EventAvailableIcon sx={{ fontSize: 44 }} />, title: 'Gestión de Citas', description: 'Calendario inteligente con recordatorios automáticos y optimización de agenda en tiempo real.', color: COLORS.navy },
    { icon: <PeopleIcon sx={{ fontSize: 44 }} />, title: 'Expedientes Digitales', description: 'Historiales clínicos completos, seguros y accesibles desde cualquier dispositivo con búsqueda avanzada.', color: COLORS.sky },
    { icon: <DuoIcon sx={{ fontSize: 44 }} />, title: 'Telemedicina', description: 'Consultas en línea con videollamadas HD seguras, chat en tiempo real y herramientas de diagnóstico.', color: COLORS.red },
    { icon: <MonetizationOnIcon sx={{ fontSize: 44 }} />, title: 'Control Financiero', description: 'Seguimiento de ingresos, facturación electrónica y reportes financieros detallados al instante.', color: COLORS.navyLight },
    { icon: <SecurityIcon sx={{ fontSize: 44 }} />, title: 'Máxima Seguridad', description: 'Encriptación de grado militar, cumplimiento HIPAA/GDPR y autenticación multifactor.', color: COLORS.skyDark },
    { icon: <CloudIcon sx={{ fontSize: 44 }} />, title: 'Acceso en la Nube', description: 'Trabaja desde cualquier lugar con sincronización automática y respaldo continuo 24/7.', color: COLORS.redDark },
    { icon: <LinkIcon sx={{ fontSize: 44 }} />, title: 'Link de Autoagendado', description: 'Comparte un enlace único para que tus pacientes agenden citas directamente, con pago integrado vía PayPal.', color: COLORS.navy },
    { icon: <SmartToyIcon sx={{ fontSize: 44 }} />, title: 'NeoMed IA — Chatbot Médico', description: 'Tu asistente de inteligencia artificial especializado en medicina. Como ChatGPT, pero diseñado exclusivamente para profesionales de la salud.', color: COLORS.red },
    { icon: <RecordVoiceOverIcon sx={{ fontSize: 44 }} />, title: 'Speech to Text AI', description: 'Transcripción automática de anamnesis por voz con IA. Dicta la historia clínica y el sistema la transcribe en tiempo real con precisión médica.', color: COLORS.sky },
  ];

  /* ── Planes ── */
  const plans = [
    {
      name: 'Plan Básico',
      price: '6.99',
      period: '/mes',
      icon: <LocalHospitalIcon sx={{ fontSize: 36 }} />,
      color: COLORS.sky,
      popular: false,
      features: [
        'Hasta 30 pacientes',
        'Acceso a funcionalidades básicas',
        'Telemedicina',
        'Soporte por correo electrónico',
        'Asistente NeoMed IA',
        'Speech to Text AI en transcripción de anamnesis',
      ],
    },
    {
      name: 'Plan Premium Mensual',
      price: '9.99',
      period: '/mes',
      icon: <WorkspacePremiumIcon sx={{ fontSize: 36 }} />,
      color: COLORS.red,
      popular: true,
      features: [
        'Hasta 250 pacientes',
        'Todas las funcionalidades básicas',
        'Reportes en PDF',
        'Receta Médica Electrónica',
        'Finanzas',
        'Recordatorios automáticos',
        'Telemedicina',
        'Asistente NeoMed IA',
        'Speech to Text AI en transcripción de anamnesis',
        'Actualizaciones',
        'Soporte prioritario',
      ],
    },
    {
      name: 'Plan Premium Anual',
      price: '105.99',
      period: '/año',
      icon: <DiamondIcon sx={{ fontSize: 36 }} />,
      color: COLORS.navy,
      popular: false,
      savings: 'Ahorro del 20%',
      features: [
        'Pacientes ilimitados',
        'Todas las funcionalidades avanzadas',
        'Reportes en PDF',
        'Receta Médica Electrónica',
        'Finanzas',
        'Recordatorios automáticos',
        'Telemedicina',
        'Asistente NeoMed IA',
        'Speech to Text AI en transcripción de anamnesis',
        'Actualizaciones',
        'Soporte 24/7',
      ],
    },
  ];

  const [securityModalOpen, setSecurityModalOpen] = useState(false);

  /* ── Benefits ── */
  const benefits = [
    { text: 'Ahorra hasta 10 horas semanales en tareas administrativas', icon: <TrendingUpIcon /> },
    { text: 'Reduce errores en el registro de pacientes en un 95%', icon: <StarIcon /> },
    { text: 'Mejora la satisfacción de tus pacientes', icon: <AutoAwesomeIcon /> },
    { text: 'Aumenta la productividad de tu consulta en un 40%', icon: <RocketLaunchIcon /> },
    { text: 'Reportes y análisis en tiempo real con IA', icon: <TrendingUpIcon /> },
    { text: 'Soporte técnico especializado 24/7 en español', icon: <SecurityIcon /> },
  ];

  /* ── Slides ── */
  const slides = [
    { image: '/mockups/dashboard.png', title: 'Dashboard Intuitivo', description: 'Visualiza toda tu información importante de un vistazo con widgets personalizables', fallback: 'https://via.placeholder.com/1920x1080/164d70/ffffff?text=Dashboard' },
    { image: '/mockups/calendar.png', title: 'Calendario Inteligente', description: 'Gestiona tus citas con facilidad, optimiza tiempos y reduce ausencias', fallback: 'https://via.placeholder.com/1920x1080/6cabd3/ffffff?text=Calendario' },
    { image: '/mockups/patients.png', title: 'Expedientes Digitales', description: 'Acceso rápido al historial completo de tus pacientes con búsqueda inteligente', fallback: 'https://via.placeholder.com/1920x1080/f3353e/ffffff?text=Pacientes' },
    { image: '/mockups/telemedicine.png', title: 'Telemedicina Integrada', description: 'Consultas virtuales de alta calidad con herramientas de diagnóstico digital', fallback: 'https://via.placeholder.com/1920x1080/164d70/ffffff?text=Telemedicina' },
    { image: '/mockups/finances.png', title: 'Control Financiero', description: 'Reportes financieros detallados, facturación electrónica y métricas en tiempo real', fallback: 'https://via.placeholder.com/1920x1080/6cabd3/ffffff?text=Finanzas' },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  /* ── Variantes de animación ── */
  const fadeInUp   = { hidden: { opacity: 0, y: 60 }, visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } } };
  const stagger    = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.14 } } };
  const scaleIn    = { hidden: { opacity: 0, scale: 0.85 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5, type: 'spring' } } };
  const slideLeft  = { hidden: { opacity: 0, x: -80 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8 } } };
  const slideRight = { hidden: { opacity: 0, x: 80 }, visible: { opacity: 1, x: 0, transition: { duration: 0.8 } } };

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */
  return (
    <Box sx={{ bgcolor: COLORS.bg, minHeight: '100vh', overflow: 'hidden' }}>

      {/* ╔══════════════════════════════════════╗
          ║           APPBAR / NAVBAR            ║
          ╚══════════════════════════════════════╝ */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: alpha(COLORS.white, 0.82),
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderBottom: `1px solid ${alpha(COLORS.sky, 0.15)}`,
          boxShadow: `0 4px 30px ${alpha(COLORS.navy, 0.06)}`,
          transition: 'all .35s ease',
        }}
      >
        <Toolbar sx={{ py: 1.2, px: { xs: 2, md: 4 } }}>
          {/* Logo + Nombre */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', flexGrow: 1, cursor: 'pointer',
              transition: 'transform .3s', '&:hover': { transform: 'scale(1.02)' }
            }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <motion.div whileHover={{ rotate: 360, scale: 1.08 }} transition={{ duration: 0.6 }}>
              <img
                src={logo}
                alt="Medify HealthCare Logo"
                style={{
                  height: isMobile ? 62 : 82,
                  width: 'auto',
                  marginRight: isMobile ? 12 : 18,
                  filter: `drop-shadow(0 4px 14px ${alpha(COLORS.navy, 0.18)})`,
                }}
              />
            </motion.div>
            <Box>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: isMobile ? '1.6rem' : '2.3rem',
                  fontFamily: '"Poppins", "Inter", sans-serif',
                  background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.sky} 60%, ${COLORS.red} 100%)`,
                  backgroundSize: '200% 200%',
                  animation: `${gradientShift} 6s ease infinite`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  letterSpacing: '-0.5px',
                  lineHeight: 1.15,
                }}
              >
                Medify HealthCare
              </Typography>
              {!isMobile && (
                <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.78rem', fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase' }}>
                  Innovación Médica
                </Typography>
              )}
            </Box>
          </Box>

          {/* Nav links */}
          {isMobile ? (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                width: 48, height: 48, borderRadius: '14px',
                bgcolor: alpha(COLORS.navy, 0.08),
                color: COLORS.navy,
                '&:hover': { bgcolor: alpha(COLORS.navy, 0.14) },
              }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {[
                { label: 'Características', id: 'features' },
                { label: 'Beneficios', id: 'benefits' },
                { label: 'Planes', id: 'pricing' },
              ].map((item) => (
                <motion.div key={item.id} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    onClick={() => scrollToSection(item.id)}
                    sx={{
                      fontWeight: 700, px: 2.5, py: 1, borderRadius: '12px',
                      color: COLORS.navy, textTransform: 'none', fontSize: '0.95rem',
                      transition: 'all .3s',
                      '&:hover': { bgcolor: alpha(COLORS.sky, 0.12), color: COLORS.navyDark },
                    }}
                  >
                    {item.label}
                  </Button>
                </motion.div>
              ))}
              <motion.div whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 800,
                    px: 4, py: 1.4, fontSize: '0.95rem',
                    bgcolor: COLORS.red, color: COLORS.white,
                    boxShadow: `0 6px 20px ${alpha(COLORS.red, 0.35)}`,
                    '&:hover': {
                      bgcolor: COLORS.redDark,
                      boxShadow: `0 8px 28px ${alpha(COLORS.red, 0.5)}`,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all .3s ease',
                  }}
                >
                  Iniciar Sesión
                </Button>
              </motion.div>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 300, bgcolor: alpha(COLORS.white, 0.95),
            backdropFilter: 'blur(24px)', borderRadius: '28px 0 0 28px',
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ bgcolor: alpha(COLORS.navy, 0.06), borderRadius: '12px' }}>
              <CloseIcon sx={{ color: COLORS.navy }} />
            </IconButton>
          </Box>
          <List>
            {['Características', 'Beneficios', 'Planes'].map((item, i) => (
              <MotionBox key={item} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemButton onClick={() => scrollToSection(item.toLowerCase())} sx={{ borderRadius: '14px', py: 1.5 }}>
                    <ListItemText primary={item} primaryTypographyProps={{ fontWeight: 700, color: COLORS.navy }} />
                  </ListItemButton>
                </ListItem>
              </MotionBox>
            ))}
            <Divider sx={{ my: 2.5, borderColor: alpha(COLORS.sky, 0.2) }} />
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Button
                fullWidth variant="contained"
                onClick={() => navigate('/login')}
                sx={{
                  borderRadius: '14px', py: 1.6, fontWeight: 800,
                  bgcolor: COLORS.red, fontSize: '1rem',
                  boxShadow: `0 6px 20px ${alpha(COLORS.red, 0.35)}`,
                  '&:hover': { bgcolor: COLORS.redDark },
                }}
              >
                Iniciar Sesión
              </Button>
            </MotionBox>
          </List>
        </Box>
      </Drawer>

      {/* ╔══════════════════════════════════════╗
          ║            HERO SECTION              ║
          ╚══════════════════════════════════════╝ */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex', alignItems: 'center',
          background: `linear-gradient(160deg, ${COLORS.navy} 0%, ${COLORS.navyDark} 40%, ${COLORS.navyLight} 100%)`,
          color: COLORS.white,
          position: 'relative', overflow: 'hidden',
          pt: { xs: 14, md: 16 },
        }}
      >
        {/* Partículas */}
        {particles.map((p) => <Particle key={p.id} {...p} />)}

        {/* Orbes decorativos */}
        <MotionBox
          animate={{ scale: [1, 1.25, 1], rotate: [0, 60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
          sx={{
            position: 'absolute', top: -120, right: -120,
            width: 500, height: 500,
            background: `radial-gradient(circle, ${alpha(COLORS.sky, 0.2)} 0%, transparent 70%)`,
            filter: 'blur(70px)',
          }}
        />
        <MotionBox
          animate={{ scale: [1, 1.35, 1], rotate: [0, -60, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
          sx={{
            position: 'absolute', bottom: -200, left: -200,
            width: 600, height: 600,
            background: `radial-gradient(circle, ${alpha(COLORS.red, 0.12)} 0%, transparent 70%)`,
            filter: 'blur(90px)',
          }}
        />

        {/* Grid pattern */}
        <Box
          sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `linear-gradient(${alpha(COLORS.white, 0.04)} 1px, transparent 1px),
                              linear-gradient(90deg, ${alpha(COLORS.white, 0.04)} 1px, transparent 1px)`,
            backgroundSize: '56px 56px',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            {/* Texto */}
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>

                <MotionBox animate={{ opacity: [0.7, 1, 0.7] }} transition={{ duration: 2.5, repeat: Infinity }}>
                  <Chip
                    label="⚡ Plataforma Médica Integral"
                    sx={{
                      bgcolor: alpha(COLORS.white, 0.12),
                      color: COLORS.white,
                      fontWeight: 800, fontSize: '0.95rem',
                      borderRadius: '12px', px: 1, py: 2.5, mb: 4,
                      border: `1px solid ${alpha(COLORS.white, 0.2)}`,
                      backdropFilter: 'blur(10px)',
                    }}
                  />
                </MotionBox>

                <MotionBox initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.35 }}>
                  <Typography
                    variant="h1"
                    sx={{
                      fontWeight: 900, mb: 4,
                      fontSize: { xs: '2.6rem', md: '4.2rem' },
                      lineHeight: 1.08,
                      fontFamily: '"Poppins","Inter",sans-serif',
                      textShadow: `0 4px 20px ${alpha(COLORS.navyDark, 0.4)}`,
                    }}
                  >
                    Gestiona tu Consultorio con{' '}
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-block', mt: 0.5,
                        background: `linear-gradient(135deg, ${COLORS.skyLight} 0%, ${COLORS.red} 100%)`,
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                      }}
                    >
                      Inteligencia
                    </Box>
                  </Typography>
                </MotionBox>

                <Typography sx={{ mb: 5, opacity: 0.92, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.75, maxWidth: 560 }}>
                  Simplifica citas, expedientes, telemedicina y finanzas en una sola plataforma.{' '}
                  <Box component="span" sx={{ fontWeight: 800, color: COLORS.skyLight }}>
                    Automatiza tu práctica médica hoy.
                  </Box>
                </Typography>

                {/* Botones CTA */}
                <MotionBox
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 7 }}
                >
                  <motion.div whileHover={{ scale: 1.05, y: -4 }} whileTap={{ scale: 0.96 }}>
                    <Button
                      variant="contained" size="large"
                      onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
                      sx={{
                        bgcolor: COLORS.red, color: COLORS.white,
                        px: 5, py: 2, fontSize: '1.15rem', fontWeight: 800, borderRadius: '16px',
                        boxShadow: `0 10px 30px ${alpha(COLORS.red, 0.45)}`,
                        '&:hover': { bgcolor: COLORS.redDark, boxShadow: `0 14px 40px ${alpha(COLORS.red, 0.55)}` },
                      }}
                    >
                      Empezar Ahora <ArrowForwardIcon sx={{ ml: 1 }} />
                    </Button>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05, y: -4 }} whileTap={{ scale: 0.96 }}>
                    <Button
                      variant="outlined" size="large"
                      component="a"
                      href="https://wa.me/13562529948?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20una%20demo%20de%20Medify%20HealthCare%20%F0%9F%A9%BA"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        borderColor: alpha(COLORS.white, 0.6), color: COLORS.white,
                        px: 5, py: 2, fontSize: '1.15rem', fontWeight: 700, borderRadius: '16px',
                        backdropFilter: 'blur(10px)', borderWidth: 2, textDecoration: 'none',
                        '&:hover': { borderColor: COLORS.white, bgcolor: alpha(COLORS.white, 0.1), borderWidth: 2 },
                      }}
                    >
                      <WhatsAppIcon sx={{ mr: 1 }} /> Agendar Demo
                    </Button>
                  </motion.div>
                </MotionBox>

                {/* Stats */}
                <MotionBox
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 1 }}
                  sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}
                >
                  {[
                    { value: '1,000+', label: 'Médicos', icon: <LocalHospitalIcon /> },
                    { value: '100K+', label: 'Pacientes', icon: <PeopleIcon /> },
                    { value: '99%', label: 'Satisfacción', icon: <FavoriteIcon /> },
                  ].map((stat, i) => (
                    <MotionBox key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + i * 0.12 }}>
                      <GlassCard sx={{ px: 3, py: 2, bgcolor: alpha(COLORS.white, 0.1), border: `1px solid ${alpha(COLORS.white, 0.18)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 0.5 }}>
                          <Box sx={{ color: COLORS.skyLight, fontSize: 22 }}>{stat.icon}</Box>
                          <Typography variant="h4" fontWeight="900" sx={{ color: COLORS.white }}>{stat.value}</Typography>
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 600 }}>{stat.label}</Typography>
                      </GlassCard>
                    </MotionBox>
                  ))}
                </MotionBox>
              </MotionBox>
            </Grid>

            {/* Imagen hero */}
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, scale: 0.85, rotate: 4 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.9, delay: 0.4, type: 'spring' }}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={doctoraHero}
                    alt="Doctor usando Medify HealthCare"
                    sx={{
                      width: '100%', height: 'auto', borderRadius: '28px',
                      boxShadow: `0 30px 60px ${alpha(COLORS.navyDark, 0.4)}, 0 0 0 1px ${alpha(COLORS.sky, 0.15)}`,
                      animation: `${float} 7s ease-in-out infinite`,
                    }}
                  />
                  {/* Badge flotante */}
                  <MotionPaper
                    sx={{
                      position: 'absolute', top: '8%', right: { xs: '4%', md: '-6%' },
                      bgcolor: COLORS.white, borderRadius: '20px', p: 2.5,
                      boxShadow: `0 12px 40px ${alpha(COLORS.navy, 0.2)}`,
                      zIndex: 2, animation: `${pulse} 3.5s ease-in-out infinite`,
                      border: `1.5px solid ${alpha(COLORS.sky, 0.15)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{
                        width: 50, height: 50, borderRadius: '14px',
                        background: `linear-gradient(135deg, ${COLORS.red} 0%, ${COLORS.redLight} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 6px 16px ${alpha(COLORS.red, 0.3)}`,
                      }}>
                        <StarIcon sx={{ color: COLORS.white, fontSize: 28 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight="900" sx={{ color: COLORS.navy }}>4.9/5</Typography>
                        <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600 }}>Rating de usuarios</Typography>
                      </Box>
                    </Box>
                  </MotionPaper>
                </Box>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ╔══════════════════════════════════════╗
          ║         SLIDESHOW / GALERÍA          ║
          ╚══════════════════════════════════════╝ */}
      <Box sx={{ py: { xs: 10, md: 14 }, bgcolor: COLORS.white }}>
        <Container maxWidth="lg">
          <MotionBox initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeInUp} sx={{ textAlign: 'center', mb: 8 }}>
            <Chip label="GALERÍA INTERACTIVA" sx={{ bgcolor: alpha(COLORS.navy, 0.08), color: COLORS.navy, fontWeight: 800, fontSize: '0.85rem', px: 2, py: 2.5, mb: 3, borderRadius: '12px' }} />
            <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' }, color: COLORS.textPrimary }}>
              Descubre Medify en Acción
            </Typography>
            <Typography variant="h6" sx={{ color: COLORS.textSecondary, maxWidth: 680, mx: 'auto' }}>
              Interfaces diseñadas intuitivamente para facilitar tu trabajo diario
            </Typography>
          </MotionBox>

          <Box sx={{ position: 'relative', width: '100%', borderRadius: '28px', overflow: 'hidden', boxShadow: `0 20px 60px ${alpha(COLORS.navy, 0.15)}, 0 0 0 1px ${alpha(COLORS.sky, 0.1)}` }}>
            <Box sx={{ position: 'relative', width: '100%', pt: '56.25%' }}>
              <Box
                sx={{
                  position: 'absolute', inset: 0,
                  '& .swiper': { height: '100%' },
                  '& .swiper-wrapper': { height: '100%' },
                  '& .swiper-slide': { height: '100%' },
                  '& .swiper-button-next, & .swiper-button-prev': {
                    color: COLORS.white,
                    background: alpha(COLORS.navy, 0.85),
                    width: 50, height: 50, borderRadius: '16px',
                    '&:after': { fontSize: '16px', fontWeight: 800 },
                    boxShadow: `0 6px 20px ${alpha(COLORS.navy, 0.3)}`,
                    transition: 'all .3s ease',
                    '&:hover': { transform: 'scale(1.1)', background: COLORS.navy },
                  },
                  '& .swiper-button-prev': { left: { xs: 10, md: 20 } },
                  '& .swiper-button-next': { right: { xs: 10, md: 20 } },
                  '& .swiper-pagination-bullet': { background: alpha(COLORS.white, 0.5), opacity: 1, width: 10, height: 10, borderRadius: '6px' },
                  '& .swiper-pagination-bullet-active': { background: COLORS.red, width: 32, borderRadius: '8px' },
                }}
              >
                <Swiper
                  modules={[Navigation, Pagination, Autoplay, EffectFade]}
                  spaceBetween={0} slidesPerView={1} navigation
                  pagination={{ clickable: true }}
                  autoplay={{ delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                  effect="fade" fadeEffect={{ crossFade: true }} loop
                >
                  {slides.map((slide, index) => (
                    <SwiperSlide key={index}>
                      <Box sx={{ position: 'relative', width: '100%', height: '100%', bgcolor: COLORS.navyDark }}>
                        <Box
                          component="img" src={slide.image} alt={slide.title}
                          sx={{ position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', backgroundColor: COLORS.navyDark }}
                          onError={(e) => { e.currentTarget.src = slide.fallback; }}
                        />
                        <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, background: `linear-gradient(to top, ${alpha(COLORS.navyDark, 0.9)} 0%, ${alpha(COLORS.navyDark, 0.3)} 40%, transparent 70%)` }} />
                        <Box sx={{ position: 'absolute', left: { xs: 20, md: 40 }, bottom: { xs: 20, md: 40 }, right: { xs: 20, md: 40 }, color: COLORS.white, zIndex: 3 }}>
                          <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.4rem' }, textShadow: `0 2px 12px ${alpha(COLORS.navyDark, 0.5)}` }}>
                            {slide.title}
                          </Typography>
                          <Typography variant="h6" sx={{ opacity: 0.92, fontSize: { xs: '1rem', md: '1.15rem' }, maxWidth: 560, lineHeight: 1.6 }}>
                            {slide.description}
                          </Typography>
                        </Box>
                        <Chip
                          label={`0${index + 1}/0${slides.length}`} size="medium"
                          sx={{
                            position: 'absolute', top: { xs: 16, md: 24 }, right: { xs: 16, md: 24 },
                            bgcolor: alpha(COLORS.red, 0.9), color: COLORS.white,
                            fontWeight: 800, fontSize: '0.85rem', backdropFilter: 'blur(10px)',
                            zIndex: 4, height: 36, borderRadius: '10px',
                          }}
                        />
                      </Box>
                    </SwiperSlide>
                  ))}
                </Swiper>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ╔══════════════════════════════════════╗
          ║        FEATURES / CARACTERÍSTICAS    ║
          ╚══════════════════════════════════════╝ */}
      <Box id="features" sx={{ py: { xs: 10, md: 14 }, bgcolor: COLORS.bg }}>
        <Container maxWidth="lg">
          <MotionBox initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeInUp} sx={{ textAlign: 'center', mb: 10 }}>
            <Chip label="CARACTERÍSTICAS" sx={{ bgcolor: alpha(COLORS.navy, 0.08), color: COLORS.navy, fontWeight: 800, fontSize: '0.85rem', px: 2, py: 2.5, mb: 3, borderRadius: '12px' }} />
            <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2rem', md: '3rem' }, color: COLORS.textPrimary }}>
              Todo lo que Necesitas en Un Solo Lugar
            </Typography>
            <Typography variant="h6" sx={{ color: COLORS.textSecondary, maxWidth: 720, mx: 'auto' }}>
              Herramientas profesionales e inteligencia artificial diseñadas para modernizar tu práctica médica
            </Typography>
          </MotionBox>

          <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
            <Grid container spacing={4}>
              {features.map((f, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <MotionCard
                    variants={scaleIn}
                    whileHover={{ y: -12, boxShadow: `0 20px 50px ${alpha(f.color, 0.18)}`, scale: 1.02 }}
                    transition={{ duration: 0.3, type: 'spring' }}
                    sx={{
                      p: 4.5, height: '100%', minHeight: 340,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
                      borderRadius: '28px', cursor: 'pointer',
                      bgcolor: COLORS.white,
                      border: `2px solid ${alpha(f.color, 0.1)}`,
                      boxShadow: `0 4px 20px ${alpha(COLORS.navy, 0.06)}`,
                      transition: 'border-color .3s, box-shadow .3s',
                      '&:hover': { border: `2px solid ${alpha(f.color, 0.35)}` },
                    }}
                  >
                    <MotionBox
                      whileHover={{ rotate: 360, scale: 1.15 }}
                      transition={{ duration: 0.6, type: 'spring' }}
                      sx={{
                        width: 88, height: 88, borderRadius: '24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(f.color, 0.1), mb: 3,
                        boxShadow: `0 8px 24px ${alpha(f.color, 0.15)}`,
                      }}
                    >
                      <Box sx={{ color: f.color }}>{f.icon}</Box>
                    </MotionBox>
                    <Typography variant="h5" fontWeight="800" gutterBottom sx={{ color: COLORS.textPrimary }}>
                      {f.title}
                    </Typography>
                    <Typography variant="body1" sx={{ color: COLORS.textSecondary, lineHeight: 1.75, flexGrow: 1, mt: 'auto' }}>
                      {f.description}
                    </Typography>
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* ╔══════════════════════════════════════╗
          ║          BENEFITS / BENEFICIOS       ║
          ╚══════════════════════════════════════╝ */}
      <Box id="benefits" sx={{ py: { xs: 10, md: 14 }, bgcolor: COLORS.white, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 0, right: 0, width: '45%', height: '100%', background: `radial-gradient(circle at 80% 50%, ${alpha(COLORS.sky, 0.06)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={slideLeft}>
                <Chip label="BENEFICIOS" sx={{ bgcolor: alpha(COLORS.navy, 0.08), color: COLORS.navy, fontWeight: 800, fontSize: '0.85rem', px: 2, py: 2.5, mb: 3, borderRadius: '12px' }} />
                <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, color: COLORS.textPrimary }}>
                  Transforma tu Práctica Médica
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: COLORS.textSecondary, fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Únete a miles de profesionales de la salud que ya confían en Medify HealthCare para gestionar sus consultorios de manera eficiente, segura y moderna.
                </Typography>

                <Box sx={{ mt: 4 }}>
                  {benefits.map((b, i) => (
                    <MotionBox
                      key={i}
                      initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08 }} viewport={{ once: true }}
                      sx={{
                        display: 'flex', gap: 2.5, mb: 2.5, alignItems: 'center',
                        p: 2, borderRadius: '16px',
                        transition: 'all .3s ease',
                        '&:hover': { bgcolor: alpha(COLORS.sky, 0.06), transform: 'translateX(8px)' },
                      }}
                    >
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                        bgcolor: alpha(COLORS.navy, 0.08),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Box sx={{ color: COLORS.navy }}>{b.icon}</Box>
                      </Box>
                      <Typography variant="body1" fontWeight={600} sx={{ color: COLORS.textPrimary, lineHeight: 1.5 }}>
                        {b.text}
                      </Typography>
                    </MotionBox>
                  ))}
                </Box>
              </MotionBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <MotionBox initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={slideRight}>
                <GlassCard sx={{ p: 5, bgcolor: alpha(COLORS.white, 0.85) }}>
                  <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 4, color: COLORS.textSecondary, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Estadísticas de Impacto
                  </Typography>
                  {[
                    { value: '+1,000', label: 'Médicos confían en Medify', icon: <PeopleIcon sx={{ fontSize: 32 }} />, color: COLORS.navy },
                    { value: '+50,000', label: 'Pacientes atendidos mensualmente', icon: <LocalHospitalIcon sx={{ fontSize: 32 }} />, color: COLORS.red },
                    { value: '99.9%', label: 'Tiempo de disponibilidad', icon: <CloudIcon sx={{ fontSize: 32 }} />, color: COLORS.sky },
                  ].map((s, i) => (
                    <Box key={i} sx={{ mb: i < 2 ? 4 : 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 1 }}>
                        <Box sx={{
                          width: 56, height: 56, borderRadius: '16px',
                          bgcolor: alpha(s.color, 0.1),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: `0 4px 14px ${alpha(s.color, 0.15)}`,
                        }}>
                          <Box sx={{ color: s.color }}>{s.icon}</Box>
                        </Box>
                        <Typography variant="h3" fontWeight="900" sx={{ color: s.color }}>{s.value}</Typography>
                      </Box>
                      <Typography variant="body1" sx={{ color: COLORS.textSecondary, ml: 9.5 }}>{s.label}</Typography>
                      {i < 2 && <Divider sx={{ mt: 3, borderColor: alpha(COLORS.sky, 0.15) }} />}
                    </Box>
                  ))}
                </GlassCard>
              </MotionBox>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ╔══════════════════════════════════════╗
          ║         PLANES / PRICING              ║
          ╚══════════════════════════════════════╝ */}
      <Box
        id="pricing"
        sx={{
          py: { xs: 10, md: 14 },
          background: `linear-gradient(160deg, ${COLORS.navy} 0%, ${COLORS.navyDark} 45%, ${COLORS.navyLight} 100%)`,
          color: COLORS.white, position: 'relative', overflow: 'hidden',
        }}
      >
        {/* Orbes decorativos */}
        <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(COLORS.red, 0.12)} 0%, transparent 70%)`, animation: `${pulse} 5s ease-in-out infinite` }} />
        <Box sx={{ position: 'absolute', bottom: -120, right: -120, width: 500, height: 500, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(COLORS.sky, 0.1)} 0%, transparent 70%)`, animation: `${pulse} 6s ease-in-out infinite` }} />
        <Box sx={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 600, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(COLORS.white, 0.03)} 0%, transparent 70%)` }} />

        {/* Grid pattern */}
        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${alpha(COLORS.white, 0.03)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(COLORS.white, 0.03)} 1px, transparent 1px)`, backgroundSize: '56px 56px' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Header */}
          <MotionBox
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}
          >
            <Chip
              label="PLANES Y PRECIOS"
              sx={{
                bgcolor: alpha(COLORS.white, 0.12), color: COLORS.white,
                fontWeight: 800, fontSize: '0.85rem', px: 2, py: 2.5, mb: 3,
                borderRadius: '12px', border: `1px solid ${alpha(COLORS.white, 0.2)}`,
                backdropFilter: 'blur(10px)',
              }}
            />
            <Typography variant="h3" fontWeight="900" gutterBottom sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, mb: 2 }}>
              Elige el Plan Perfecto para Ti
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontSize: { xs: '1.05rem', md: '1.2rem' }, lineHeight: 1.7, maxWidth: 620, mx: 'auto' }}>
              Comienza con{' '}
              <Box component="span" sx={{ fontWeight: 800, color: COLORS.skyLight }}>30 días de prueba gratuita</Box>.
              Sin tarjeta de crédito. Cancela cuando quieras.
            </Typography>
          </MotionBox>

          {/* Cards de planes */}
          <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }}>
            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
              {plans.map((plan, i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <MotionCard
                    variants={scaleIn}
                    whileHover={{ y: -14, scale: 1.02, boxShadow: `0 28px 60px ${alpha(plan.color, 0.25)}` }}
                    transition={{ duration: 0.3, type: 'spring' }}
                    sx={{
                      height: '100%', display: 'flex', flexDirection: 'column',
                      borderRadius: '28px', overflow: 'visible', position: 'relative',
                      bgcolor: COLORS.white, color: COLORS.textPrimary,
                      border: plan.popular ? `3px solid ${COLORS.red}` : `2px solid ${alpha(COLORS.sky, 0.15)}`,
                      boxShadow: plan.popular
                        ? `0 20px 50px ${alpha(COLORS.red, 0.2)}, 0 0 0 1px ${alpha(COLORS.red, 0.1)}`
                        : `0 10px 40px ${alpha(COLORS.navy, 0.1)}`,
                      transition: 'border-color .3s, box-shadow .3s',
                    }}
                  >
                    {/* Badge popular */}
                    {plan.popular && (
                      <Chip
                        label="⭐ MÁS POPULAR"
                        sx={{
                          position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)',
                          bgcolor: COLORS.red, color: COLORS.white,
                          fontWeight: 800, fontSize: '0.8rem', px: 1.5, height: 32,
                          borderRadius: '10px',
                          boxShadow: `0 6px 20px ${alpha(COLORS.red, 0.4)}`,
                        }}
                      />
                    )}

                    {/* Savings badge */}
                    {plan.savings && (
                      <Chip
                        label={`🎉 ${plan.savings}`}
                        sx={{
                          position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)',
                          bgcolor: COLORS.navy, color: COLORS.white,
                          fontWeight: 800, fontSize: '0.8rem', px: 1.5, height: 32,
                          borderRadius: '10px',
                          boxShadow: `0 6px 20px ${alpha(COLORS.navy, 0.4)}`,
                        }}
                      />
                    )}

                    {/* Header del plan */}
                    <Box sx={{ p: { xs: 3.5, md: 4.5 }, pb: 0, textAlign: 'center' }}>
                      <Box sx={{
                        width: 72, height: 72, borderRadius: '20px', mx: 'auto', mb: 3,
                        background: `linear-gradient(135deg, ${alpha(plan.color, 0.15)} 0%, ${alpha(plan.color, 0.05)} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 8px 24px ${alpha(plan.color, 0.15)}`,
                      }}>
                        <Box sx={{ color: plan.color }}>{plan.icon}</Box>
                      </Box>

                      <Typography variant="h6" fontWeight="800" sx={{ color: COLORS.textPrimary, mb: 1.5 }}>
                        {plan.name}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: COLORS.textSecondary, mr: 0.5 }}>$</Typography>
                        <Typography sx={{ fontSize: '3.2rem', fontWeight: 900, color: plan.color, lineHeight: 1 }}>
                          {plan.price}
                        </Typography>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: COLORS.textSecondary, ml: 0.5 }}>
                          {plan.period}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ mx: 4, my: 2.5, height: '1.5px', background: `linear-gradient(90deg, transparent, ${alpha(plan.color, 0.2)}, transparent)` }} />

                    {/* Features del plan */}
                    <Box sx={{ px: { xs: 3.5, md: 4.5 }, pb: 2, flex: 1 }}>
                      {plan.features.map((feat, fi) => (
                        <MotionBox
                          key={fi}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.05 * fi }}
                          viewport={{ once: true }}
                          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.8 }}
                        >
                          <CheckCircleIcon sx={{ color: plan.color, fontSize: 20, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.5, fontWeight: 500 }}>
                            {feat}
                          </Typography>
                        </MotionBox>
                      ))}
                    </Box>

                    {/* Botón CTA */}
                    <Box sx={{ px: { xs: 3.5, md: 4.5 }, pb: { xs: 3.5, md: 4.5 } }}>
                      <motion.div whileHover={{ scale: 1.03, y: -2 }} whileTap={{ scale: 0.97 }}>
                        <Button
                          fullWidth variant="contained" size="large"
                          onClick={() => navigate('/login')}
                          sx={{
                            py: 1.8, fontSize: '1rem', fontWeight: 800,
                            borderRadius: '16px', textTransform: 'none',
                            bgcolor: plan.popular ? COLORS.red : plan.color,
                            color: COLORS.white,
                            boxShadow: `0 8px 24px ${alpha(plan.popular ? COLORS.red : plan.color, 0.35)}`,
                            '&:hover': {
                              bgcolor: plan.popular ? COLORS.redDark : alpha(plan.color, 0.85),
                              boxShadow: `0 12px 32px ${alpha(plan.popular ? COLORS.red : plan.color, 0.5)}`,
                            },
                            transition: 'all .3s ease',
                          }}
                        >
                          Comenzar Ahora
                        </Button>
                      </motion.div>
                    </Box>
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </MotionBox>

          {/* Nota inferior */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: 0.5 }}
            sx={{ textAlign: 'center', mt: 8 }}
          >
            <Typography variant="body1" sx={{ opacity: 0.85, fontWeight: 600, mb: 1 }}>
              ✓ Sin compromisos &nbsp;&nbsp; ✓ Soporte incluido &nbsp;&nbsp; ✓ Cancelación fácil
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              Prueba gratuita con hasta 5 pacientes. Los precios están en USD.
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* ╔══════════════════════════════════════╗
          ║              FOOTER                  ║
          ╚══════════════════════════════════════╝ */}
      <Box sx={{ py: 8, bgcolor: COLORS.white, borderTop: `1px solid ${alpha(COLORS.sky, 0.12)}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Logo + descripción */}
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <motion.div whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>
                    <img
                      src={logo} alt="Medify HealthCare Logo"
                      style={{ height: 60, marginRight: 14, filter: `drop-shadow(0 4px 10px ${alpha(COLORS.navy, 0.15)})` }}
                    />
                  </motion.div>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900, fontSize: '1.5rem',
                        fontFamily: '"Poppins","Inter",sans-serif',
                        background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.sky} 100%)`,
                        WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                      }}
                    >
                      Medify HealthCare
                    </Typography>
                    <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.72rem', fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                      Innovación Médica
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.8, fontSize: '0.95rem', maxWidth: 420 }}>
                  La plataforma integral y moderna para la gestión eficiente de consultorios médicos. Transforma tu práctica con tecnología de vanguardia.
                </Typography>
              </MotionBox>
            </Grid>

            {/* Producto + Redes */}
            <Grid item xs={12} md={3}>
              <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
                <Typography variant="subtitle2" fontWeight="800" gutterBottom sx={{ mb: 2.5, color: COLORS.textPrimary, fontSize: '0.95rem' }}>
                  Producto
                </Typography>
                {[
                  { label: 'Características', action: () => scrollToSection('features') },
                  { label: 'Precios',         action: () => scrollToSection('pricing') },
                  { label: 'Seguridad',       action: () => setSecurityModalOpen(true) },
                  {
                    label: 'Agendar Demo',
                    action: () => window.open(
                      'https://wa.me/13562529948?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20una%20demo%20de%20Medify%20HealthCare%20%F0%9F%A9%BA',
                      '_blank',
                      'noopener,noreferrer'
                    ),
                  },
                ].map((item) => (
                  <motion.div key={item.label} whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <Typography
                      variant="body2"
                      onClick={item.action}
                      sx={{
                        color: COLORS.textSecondary, cursor: 'pointer', mb: 1.8, display: 'block',
                        transition: 'color .3s', '&:hover': { color: COLORS.navy },
                      }}
                    >
                      {item.label}
                    </Typography>
                  </motion.div>
                ))}
              </MotionBox>
            </Grid>

            {/* Redes sociales */}
            <Grid item xs={12} md={3}>
              <MotionBox initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
                <Typography variant="subtitle2" fontWeight="800" gutterBottom sx={{ mb: 2.5, color: COLORS.textPrimary, fontSize: '0.95rem' }}>
                  Síguenos
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {[
                    { icon: <InstagramIcon />, href: 'https://www.instagram.com/app.medifyhc' },
                    { icon: <XIcon />, href: 'https://x.com/MedifyHC' },
                    { icon: <PinterestIcon />, href: 'https://www.pinterest.com/MedifyHC' },
                  ].map((social, i) => (
                    <motion.div key={i} whileHover={{ scale: 1.12, y: -3 }} whileTap={{ scale: 0.9 }}>
                      <IconButton
                        component="a" href={social.href} target="_blank"
                        sx={{
                          width: 48, height: 48, borderRadius: '14px',
                          bgcolor: alpha(COLORS.navy, 0.06), color: COLORS.navy,
                          transition: 'all .3s',
                          '&:hover': { bgcolor: alpha(COLORS.navy, 0.12) },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>
              </MotionBox>
            </Grid>
          </Grid>

          <Divider sx={{ my: 5, borderColor: alpha(COLORS.sky, 0.12) }} />

          <MotionBox initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
            <Typography variant="body2" align="center" sx={{ color: COLORS.textSecondary, opacity: 0.7, fontWeight: 500 }}>
              © {new Date().getFullYear()} Medify HealthCare. Todos los derechos reservados. Hecho con{' '}
              <FavoriteIcon sx={{ fontSize: 14, color: COLORS.red, verticalAlign: 'middle', mx: 0.3 }} />{' '}
              para profesionales de la salud.
            </Typography>
          </MotionBox>
        </Container>
      </Box>

      {/* Formulario de Contacto */}
      <ContactForm />

      {/* ╔══════════════════════════════════════╗
          ║         MODAL DE SEGURIDAD           ║
          ╚══════════════════════════════════════╝ */}
      <Dialog
        open={securityModalOpen}
        onClose={() => setSecurityModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '28px',
            overflow: 'hidden',
            boxShadow: `0 30px 80px ${alpha(COLORS.navy, 0.2)}`,
          },
        }}
      >
        {/* Header del modal */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%)`,
            px: 4, py: 3.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px',
              bgcolor: alpha(COLORS.white, 0.15),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldIcon sx={{ color: COLORS.white, fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="900" sx={{ color: COLORS.white, lineHeight: 1.2 }}>
                Seguridad & Certificaciones
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(COLORS.white, 0.75), fontWeight: 600 }}>
                Medify HealthCare
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setSecurityModalOpen(false)}
            sx={{
              color: COLORS.white,
              bgcolor: alpha(COLORS.white, 0.1),
              borderRadius: '12px',
              '&:hover': { bgcolor: alpha(COLORS.white, 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenido */}
        <DialogContent sx={{ px: 4, py: 4, bgcolor: COLORS.bg }}>

          {/* Intro */}
          <Typography variant="body1" sx={{ color: COLORS.textSecondary, lineHeight: 1.8, mb: 4 }}>
            En <Box component="span" fontWeight="800" sx={{ color: COLORS.navy }}>MedifyHC</Box>, nos comprometemos
            con los más altos estándares de seguridad web. Nuestro sitio ha sido verificado y certificado
            por múltiples autoridades de seguridad independientes.
          </Typography>

          {/* Certificaciones */}
          {[
            {
              title: 'Google Safe Browsing',
              badge: 'No unsafe content found',
              description: 'Verificado como sitio seguro sin contenido malicioso.',
              color: COLORS.navy,
              icon: <VerifiedIcon />,
            },
            {
              title: 'SSL Labs',
              badge: 'Calificación "A"',
              description: 'Implementación óptima de seguridad SSL/TLS.',
              color: COLORS.sky,
              icon: <SecurityIcon />,
            },
            {
              title: 'VirusTotal',
              badge: 'Clean',
              description: 'Sin detección de malware o amenazas.',
              color: '#2e7d32',
              icon: <VerifiedIcon />,
            },
          ].map((cert, i) => (
            <MotionBox
              key={i}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              sx={{
                display: 'flex', gap: 2.5, alignItems: 'flex-start',
                p: 2.5, mb: 2, borderRadius: '18px',
                bgcolor: COLORS.white,
                border: `1.5px solid ${alpha(cert.color, 0.15)}`,
                boxShadow: `0 4px 16px ${alpha(cert.color, 0.07)}`,
                transition: 'all .3s',
                '&:hover': {
                  border: `1.5px solid ${alpha(cert.color, 0.35)}`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(cert.color, 0.12)}`,
                },
              }}
            >
              {/* Ícono */}
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px', flexShrink: 0,
                bgcolor: alpha(cert.color, 0.1),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha(cert.color, 0.15)}`,
              }}>
                <Box sx={{ color: cert.color }}>{cert.icon}</Box>
              </Box>

              {/* Texto */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                  <Typography variant="body1" fontWeight="800" sx={{ color: COLORS.textPrimary }}>
                    {cert.title}
                  </Typography>
                  <Chip
                    label={cert.badge}
                    size="small"
                    sx={{
                      bgcolor: alpha(cert.color, 0.1),
                      color: cert.color,
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      height: 24,
                      borderRadius: '8px',
                      border: `1px solid ${alpha(cert.color, 0.2)}`,
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, lineHeight: 1.6 }}>
                  {cert.description}
                </Typography>
              </Box>
            </MotionBox>
          ))}

          {/* Nota final */}
          <MotionBox
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            sx={{
              mt: 3, p: 3, borderRadius: '18px',
              background: `linear-gradient(135deg, ${alpha(COLORS.navy, 0.06)} 0%, ${alpha(COLORS.sky, 0.06)} 100%)`,
              border: `1.5px solid ${alpha(COLORS.navy, 0.1)}`,
              display: 'flex', gap: 2, alignItems: 'center',
            }}
          >
            <Box sx={{
              width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
              bgcolor: alpha(COLORS.navy, 0.1),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShieldIcon sx={{ color: COLORS.navy, fontSize: 24 }} />
            </Box>
            <Typography variant="body2" fontWeight="600" sx={{ color: COLORS.textPrimary, lineHeight: 1.7 }}>
              La información de tus pacientes está{' '}
              <Box component="span" fontWeight="900" sx={{ color: COLORS.navy }}>
                totalmente encriptada
              </Box>{' '}
              y protegida bajo los más altos estándares internacionales.
            </Typography>
          </MotionBox>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LandingPage;